import {
  createSelectorHooks,
  type ZustandHookSelectors,
} from 'auto-zustand-selectors-hook';
import { create } from 'zustand';

import {
  removeFacetFromSelection,
  type FacetSelection,
} from '$/utils/facetFilter';

export type HarmonyFacet =
  | 'scene'
  | 'mood'
  | 'tonality'
  | 'sceneType'
  | 'colorRange';

export type HarmonyViewMode = 'color' | 'scene';

type State = {
  selection: FacetSelection<HarmonyFacet>;
  viewMode: HarmonyViewMode;
};

type Actions = {
  setSingleFilter: (facet: HarmonyFacet, value: string) => void;
  toggleSingleFilter: (facet: HarmonyFacet, value: string) => void;
  toggleMultiFilter: (facet: HarmonyFacet, value: string) => void;
  setViewMode: (viewMode: HarmonyViewMode) => void;
  reset: (facet?: HarmonyFacet) => void;
  isActive: (facet: HarmonyFacet, value: string) => boolean;
};

const initial: State = {
  selection: { scene: 'exterior' },
  viewMode: 'color',
};

const store = create<State & Actions>()((set, get) => ({
  ...initial,

  setSingleFilter: (facet, value) =>
    set((state) => {
      if (facet === 'scene' && state.selection.scene !== value) {
        return {
          selection: {
            ...removeFacetFromSelection(
              state.selection,
              'tonality',
              'sceneType',
            ),
            [facet]: value,
          },
        };
      }

      return {
        selection: { ...state.selection, [facet]: value },
      };
    }),

  toggleSingleFilter: (facet, value) =>
    set((state) => {
      const without = removeFacetFromSelection(state.selection, facet);
      return {
        selection:
          state.selection[facet] === value
            ? without
            : { ...without, [facet]: value },
      };
    }),

  toggleMultiFilter: (facet, value) =>
    set((state) => {
      const current = state.selection[facet];
      const arr = Array.isArray(current) ? current : [];
      const nextArr = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];

      const without = removeFacetFromSelection(state.selection, facet);
      if (nextArr.length === 0) return { selection: without };
      return { selection: { ...without, [facet]: nextArr } };
    }),

  setViewMode: (viewMode) =>
    set((state) => {
      if (viewMode !== 'scene') {
        return {
          viewMode,
          selection: removeFacetFromSelection(state.selection, 'sceneType'),
        };
      }

      return { viewMode };
    }),

  reset: (facet) =>
    set((state) => {
      if (facet === undefined) {
        return {
          selection: {
            scene: state.selection.scene ?? initial.selection.scene,
          },
        };
      }
      if (facet === 'scene') {
        return {
          selection: { ...state.selection, scene: initial.selection.scene },
        };
      }
      return { selection: removeFacetFromSelection(state.selection, facet) };
    }),

  isActive: (facet, value) => {
    const current = get().selection[facet];
    if (current === undefined) return false;
    if (Array.isArray(current)) return current.includes(value);
    return current === value;
  },
}));

export const useDesignGuideStore = createSelectorHooks(store) as typeof store &
  ZustandHookSelectors<State & Actions>;
